import {
  type CreateReference,
  createCodeableConcept,
  createExtensionValue,
  createFhirDate,
  createMapWithId,
  ExtensionUrl,
  filterExtensionValues,
  findExtensionValue,
  getFirstCodeFromCodableConcepts,
  getIdQueryString,
  isValidReference,
  mapFhirToModelPeriod,
  type TaskCode,
  type TaskReferenceType,
  updateExtensions,
  valsOrNone,
  type WithId,
} from '@quromedical/fhir-common';
import { compact } from '@quromedical/utils';
import type { Extension, Period } from 'fhir/r4';
import { getValidReference } from '../mappings/reference';
import {
  mapFhirToResourceNote,
  mapResourceNoteToFhir,
} from '../resource-note/mapping';

import {
  type CustomTaskStatuses,
  type PartialTask,
  type TaskNote,
  type TaskReference,
  type TaskSearchStatus,
  type TaskStatus,
  type TaskUpdate,
  type TaskUpdateOverdue,
  TaskUpdateOverdueSchema,
  type TaskUpdateStatus,
  TaskUpdateStatusSchema,
} from './schema';

type FhirTaskReference = {
  type: fhir4.CodeableConcept;
  valueReference: CreateReference;
};

const mapTaskReferenceToFhir = (
  taskReference: TaskReference,
): FhirTaskReference => ({
  type: createCodeableConcept(taskReference.type),
  valueReference: taskReference.value,
});

const mapFhirToTaskReference = (
  fhir: fhir4.TaskInput | fhir4.TaskOutput,
): TaskReference | undefined => {
  const value = getValidReference(fhir.valueReference);
  const type = getFirstCodeFromCodableConcepts<TaskReferenceType>([fhir.type]);
  const hasData = value && type;

  if (!hasData) {
    return undefined;
  }

  return {
    type,
    value,
  };
};

const createRequestedPeriodExtension = (requestedPeriod: Period): Extension =>
  createExtensionValue(ExtensionUrl.TaskRequestedPeriod)(requestedPeriod);

const createRequestedPerformerExtension = (
  performers: CreateReference[],
): Extension[] =>
  performers.map((performer) => {
    return createExtensionValue(ExtensionUrl.TaskRequestedPerformer)(performer);
  });

const createExtensionValues = (
  extensions: TaskExtensionValues,
): Extension[] => {
  const requestedPeriodExtension = extensions.requestedPeriod
    ? createRequestedPeriodExtension(extensions.requestedPeriod)
    : undefined;

  const requestedPerformerExtension = createRequestedPerformerExtension(
    extensions.performers,
  );

  const result = requestedPeriodExtension
    ? [requestedPeriodExtension, ...requestedPerformerExtension]
    : requestedPerformerExtension;

  return result;
};

export const mapTaskToFhir = (model: PartialTask): fhir4.Task => {
  const {
    input,
    intent,
    notes,
    output,
    status,
    admission,
    code,
    created,
    description,
    focus,
    for: taskFor,
    owner,
    period,
    requester,
    updated,
    requestedPeriod,
    performers,
  } = model;

  const extension = createExtensionValues({ requestedPeriod, performers });

  return {
    resourceType: 'Task',
    intent,
    status,
    authoredOn: created,
    lastModified: updated,
    description,
    encounter: admission,
    for: taskFor,
    focus,
    owner,
    executionPeriod: period,
    requester,
    code: code ? createCodeableConcept(code) : undefined,
    output: valsOrNone(output.map(mapTaskReferenceToFhir)),
    input: valsOrNone(input.map(mapTaskReferenceToFhir)),
    note: valsOrNone(notes.map(mapResourceNoteToFhir)),
    extension,
  };
};

type TaskExtensionValues = Pick<PartialTask, 'requestedPeriod' | 'performers'>;

const getRequestedPeriodExtensionValue = findExtensionValue(
  ExtensionUrl.TaskRequestedPeriod,
);
const getPerformersExtensionValue = filterExtensionValues(
  ExtensionUrl.TaskRequestedPerformer,
);

export const getValuesFromExtensions = (
  extensions?: Extension[],
): TaskExtensionValues => {
  const performers = getPerformers(extensions).filter(isValidReference);

  return {
    requestedPeriod: getRequestedPeriod(extensions),
    performers: performers ? performers : [],
  };
};

const getRequestedPeriod = (extension?: Extension[]) =>
  mapFhirToModelPeriod(getRequestedPeriodExtensionValue(extension));

const getPerformers = (extension?: Extension[]) =>
  getPerformersExtensionValue(extension);

export const mapFhirToTask = (fhir: fhir4.Task): PartialTask => {
  const {
    intent,
    status,
    authoredOn,
    lastModified,
    description,
    encounter,
    for: taskFor,
    focus,
    owner,
    executionPeriod,
    requester,
    code,
    output = [],
    input = [],
    note = [],
    extension,
  } = fhir;

  const extensionValues = getValuesFromExtensions(extension);

  return {
    requestedPeriod: extensionValues.requestedPeriod,
    intent,
    status,
    description,
    created: authoredOn,
    updated: lastModified,
    admission: getValidReference(encounter),
    for: getValidReference(taskFor),
    code: getFirstCodeFromCodableConcepts<TaskCode>(code ? [code] : undefined),
    focus: getValidReference(focus),
    owner: getValidReference(owner),
    period: mapFhirToModelPeriod(executionPeriod),
    requester: getValidReference(requester),
    notes: compact(note.map(mapFhirToResourceNote)),
    input: compact(input.map(mapFhirToTaskReference)),
    output: compact(output.map(mapFhirToTaskReference)),
    performers: extensionValues.performers,
  };
};

export const mapFhirToTaskWithId = createMapWithId(mapFhirToTask);
export const mapTaskToFhirWithId = createMapWithId(mapTaskToFhir);

const createPeriodExtension = (
  period: Period,
  existingExtensions?: Extension[],
) =>
  updateExtensions(ExtensionUrl.TaskRequestedPeriod, existingExtensions, [
    createExtensionValue(ExtensionUrl.TaskRequestedPeriod)(period),
  ]);

export const customStatusToFhirMap: Record<CustomTaskStatuses, TaskStatus[]> = {
  active: [
    'requested',
    'received',
    'accepted',
    'ready',
    'in-progress',
    'on-hold',
    'failed',
  ],
  inactive: ['completed', 'draft', 'rejected', 'cancelled', 'entered-in-error'],
  overdue: ['failed'],
};

const customStatuses = Object.keys(customStatusToFhirMap);

const isCustomTaskStatus = (
  status: TaskSearchStatus,
): status is CustomTaskStatuses => customStatuses.includes(status);

export const getTaskStatusQuery = (
  status?: TaskSearchStatus,
): string | undefined => {
  if (!status) {
    return undefined;
  }

  if (!isCustomTaskStatus(status)) {
    return status;
  }

  return getIdQueryString(customStatusToFhirMap[status]);
};

export const updateFhirTaskRequestedPeriod = (
  dueDate: Date,
  extension: Extension[] = [],
  authoredOn?: string,
): Extension[] | undefined => {
  const startDate = authoredOn ? new Date(authoredOn) : new Date();

  const period = {
    start: createFhirDate(startDate),
    end: createFhirDate(dueDate),
  };

  return createPeriodExtension(period, extension);
};

export const updateFhirTaskStatus = (
  task: fhir4.Task,
  status: TaskStatus,
): fhir4.Task => ({
  ...task,
  status: status,
});

export const updateFhirTaskNote = (
  task: fhir4.Task,
  note: TaskNote,
): fhir4.Task => ({
  ...task,
  note: task.note ? [...task.note, note] : [note],
});

const isTaskUpdateOverdue = (input: TaskUpdate): input is TaskUpdateOverdue => {
  return TaskUpdateOverdueSchema.safeParse(input).success;
};

const isTaskUpdateStatus = (input: TaskUpdate): input is TaskUpdateStatus => {
  return TaskUpdateStatusSchema.safeParse(input).success;
};

export const mapFhirTaskUpdate = (
  fhirTask: WithId<fhir4.Task>,
  input: TaskUpdate,
): WithId<fhir4.Task> => {
  const task = Object.assign({}, fhirTask);

  if (isTaskUpdateStatus(input)) {
    task.status = input.status;
  }

  if (isTaskUpdateOverdue(input)) {
    task.extension = updateFhirTaskRequestedPeriod(
      input.dueDate,
      fhirTask.extension,
      fhirTask.authoredOn,
    );
  }

  return task;
};
